import { Column, Entity, Index } from 'typeorm';
import { BaseEntity } from './base.entity';

/**
 * PD-14270 — a named, reusable voicemail-drop template a rep picks when they
 * reach voicemail on a MANUAL outbound call and want to leave a pre-rendered
 * AI voicemail.
 *
 * Deliberately standalone — NOT tied to a ConversationAgent or a campaign.
 * It carries only the three things the drop needs: the message (with
 * `{{placeholder}}` markers), which voice speaks it, and a name to pick it by.
 *
 * Placeholders supported in `message` (rendered per-recipient at drop time
 * from the live call context):
 *   - {{repFirstName}} / {{repLastName}}     — the calling rep
 *   - {{tenantCompany}}                       — the rep's company (this tenant)
 *   - {{contactFirstName}} / {{contactLastName}} — the person being called
 *   - {{contactCompany}}                      — the company being called
 * Unknown / unresolved placeholders are left blank.
 */
@Entity('voicemail_template')
export class VoicemailTemplateEntity extends BaseEntity {
	/**
	 * Display name the rep picks the template by. NOT unique: templates are
	 * owner-scoped and a SHARED one may collide names with another rep's — the
	 * UI disambiguates with an owner chip. (The pre-existing unique index is
	 * dropped by DropVoicemailTemplateNameUniqueIndex.)
	 */
	@Column({ type: 'varchar', length: 255 })
	public name!: string;

	/** Optional admin-facing description. */
	@Column({ type: 'text', nullable: true })
	public description?: string;

	/** The message body, with `{{placeholder}}` markers. */
	@Column({ type: 'longtext' })
	public message!: string;

	/**
	 * The voice that speaks the rendered message — FK to
	 * `_conversation_voice_profiles`. Required: a template can't be synthesized
	 * without a voice. (No FK constraint to keep DB-sync additive, mirroring the
	 * codebase's convention of plain FK columns.)
	 */
	@Column('int', {
		name: 'fk_conversation_voice_profile__id',
		unsigned: true,
	})
	public fkVoiceProfileId!: number;

	/** Inactive templates are hidden from the rep's picker but kept for audit. */
	@Column({ name: 'isActive', type: 'boolean', default: true })
	@Index()
	public isActive!: boolean;

	/**
	 * Private by default. When `true`, the template is shared tenant-wide: every
	 * rep sees it in their picker and can DROP it, but only the owner
	 * (`_owner_userId`) can edit or delete it. Indexed to keep the
	 * "mine OR shared" list filter cheap.
	 */
	@Column({ name: 'isShared', type: 'boolean', default: false })
	@Index()
	public isShared!: boolean;
}
